import React, {
    useState,
    useEffect,
    useContext,
    useRef
} from "react";

import useConfirmationModal
    from "../../hooks/useConfirmation";

import LeftMenu from "../components/LeftMenu";
import ConfirmationModal
    from "../components/ConfirmationModal";

import db from "../../constants/db";

import { store } from "../../context/store.js";

import {
    CHANGE_OBSERVATION_HOUR
} from "../../constants/action-types";

// COMPONENTS
import FloorHeader from "./components/FloorHeader";
import FloorCanvas from "./components/FloorCanvas";
import FloorPopupController from "./components/FloorPopupController";
import SurveyProgress from "../components/SurveyProgress";

// HOOKS
// TEMP DISABLED FOR DEBUGGING
import {
     useUtilisation
 } from "./hooks/useUtilisation";
 
 
 

export default function Floor() {

    const [workplaces, setWorkplaces] = useState([]);

    const [workplaceTypes, setWorkplaceTypes] = useState([]);

    const [activities, setActivities] = useState([]);

    const [clickStates, setClickStates] = useState({});
	const [pill, setPill] = useState(null);

    const [showUnprocessed, setShowUnprocessed] = useState(false);

    const [popupDesk, setPopupDesk] = useState(null);

    const [floorName, setFloorName] = useState("");

    const [imgUrl, setImgUrl] = useState("");

    const [floorPlanHeight, setFloorPlanHeight] = useState(0);

    const [floorPlanWidth, setFloorPlanWidth] = useState(0);

    const [initialScale, setInitialScale] = useState(1);

    const [submitted, setSubmitted] = useState(false);

    const [showSubmittedLabel, setShowSubmittedLabel] = useState(false);

    const [showSubmitButton, setShowSubmitButton] = useState(true);

    const [floorCount, setFloorCount] = useState(0);

    const globalState = useContext(store);

    const { dispatch } = globalState;

    const containerRef = useRef(null);
	const pillTimeoutRef = useRef(null);
	
	const {
	    confirm,
	    modalProps,
	    handleModalConfirm,
	    handleModalCancel
	} = useConfirmationModal();
	
	

    /**************************************************************************
     * SAFE GLOBAL STATE REFERENCES
     *************************************************************************/

    const selectedFloor =
        globalState?.state?.selectedFloor;

    const selectedFloorId =
        selectedFloor?.id;

		const observationHour =
		    Number(
		        globalState.state.observationHour ??
		        new Date().getHours()
		    );
	

    /**************************************************************************
     * TEMP DEBUG STUBS
     *************************************************************************/

	const {
	    saveUtilisation,
	    handleSubmit,
		confirmModal
	} = useUtilisation({
	    db,
	    globalState,
	    dispatch,
	    workplaces,
	    clickStates,
	    setClickStates,
	    setSubmitted,
	    setShowSubmittedLabel,
	    setShowSubmitButton
	});
	
	useEffect(function () {

	    dispatch({
	        type: CHANGE_OBSERVATION_HOUR,
	        value: new Date().getHours()
	    });

	}, []);
	
	
	useEffect(function () {

	  setPill(null);

	}, [showUnprocessed]);
	

    /**************************************************************************
     * Load Floor Data
     *************************************************************************/

    useEffect(function () {

        let mounted = true;

        async function loadData() {

            if (!selectedFloorId) {

                return;
            }


            const desks = await db.workplaces
                .where("floorId")
                .equals(selectedFloorId)
                .toArray();

            const acts =
                await db.activities.toArray();

            const types =
                await db.workplaceTypes.toArray();

            const desksWithSvg = desks.filter(function (d) {

                return (
                    d.svg &&
                    d.svg.trim().length > 0
                );
            });

            const count =
                await db.floors.count();

            if (!mounted) {
                return;
            }

            setFloorCount(count);

            setWorkplaces(desksWithSvg);

            setActivities(acts);

            setWorkplaceTypes(types);

            const fullFloor =
                await db.floors.get(selectedFloorId);

            if (
                fullFloor &&
                fullFloor.dwg
            ) {

                setImgUrl(
                    "data:image/jpeg;base64," +
                    fullFloor.dwg
                );
            }

            setFloorName(
                selectedFloor?.displayCode || ""
            );

            setFloorPlanHeight(
                parseInt(selectedFloor?.height, 10) || 0
            );

            setFloorPlanWidth(
                parseInt(selectedFloor?.width, 10) || 0
            );
        }

        loadData();

        return function () {

            mounted = false;
        };

    }, [selectedFloorId]);

    /**************************************************************************
     * Calculate Initial Scale
     *************************************************************************/

    useEffect(function () {

        if (
            !floorPlanWidth ||
            !floorPlanHeight
        ) {
            return;
        }

        const el = containerRef.current;

        if (!el) {

            return;
        }

        function calculateScale() {

            const scale = Math.min(
                el.clientWidth / floorPlanWidth,
                el.clientHeight / floorPlanHeight
            );

            setInitialScale(
                scale > 0 ? scale : 1
            );
        }

        calculateScale();

        const resizeObserver =
            new ResizeObserver(calculateScale);

        resizeObserver.observe(el);

        return function () {

            resizeObserver.disconnect();
        };

    }, [
        floorPlanWidth,
        floorPlanHeight
    ]);

	/**************************************************************************
	 * Get Record Date
	 *************************************************************************/

	function getRecordDate(timestamp) {

	    if (!timestamp) {

	        return null;
	    }

	    if (typeof timestamp === "string") {

	        return timestamp.split("T")[0];
	    }

	    if (timestamp instanceof Date) {

	        return timestamp
	            .toISOString()
	            .split("T")[0];
	    }

	    return null;
	}

	/**************************************************************************
	 * Restore Existing Survey State
	 *************************************************************************/

	useEffect(function () {
		
	    let mounted = true;

	    async function restoreDeskState() {

	        if (!selectedFloorId) {

	            return;
	        }

			if (
			    !workplaces ||
			    workplaces.length === 0
			) {
			    return;
			}

	        const today =
	            new Date()
	                .toISOString()
	                .split("T")[0];

	        const existing =
	            await db.utilisation
	                .where("floorId")
	                .equals(selectedFloorId)
	                .and(function (r) {

	                    const d =
	                        getRecordDate(r.timestamp);

	                    return (
	                        d === today &&
	                        Number(r.observationHour) === observationHour
	                    );
	                })
	                .toArray();
					
			

	        const restoredClickStates = {};

	        const validIds =
	            new Set(
	                workplaces.map(function (w) {

	                    return w.id;
	                })
	            );

	        existing.forEach(function (r) {

	            if (
	                validIds.has(r.spaceId)
	            ) {

	                restoredClickStates[r.spaceId] =
	                    r.occupiedVacantSol || 0;
	            }
	        });

	        const restoredWorkplaces =
	            workplaces.map(function (w) {

	                const existingRow =
	                    existing.find(function (r) {

	                        return r.spaceId === w.id;
	                    });

	                if (!existingRow) {

	                    return w;
	                }

	                return {
	                    ...w,

	                    selectedActivityId:
	                        existingRow.activityId || null,

	                    capacity:
	                        existingRow.capacityUsed || 0
	                };
	            });

	        if (!mounted) {

	            return;
	        }

	        setClickStates(
	            restoredClickStates
	        );

	        setWorkplaces(
	            restoredWorkplaces
	        );
	    }

	    restoreDeskState();

	    return function () {

	        mounted = false;
	    };

	}, [
	    selectedFloorId,
	    observationHour,
	    workplaces.length
	]);
	
	
	/**************************************************************************
	 * Submit Click
	 *************************************************************************/

	async function handleSubmitClick() {
		setPill(null);
	    if (processedCount < total) {

	        const remaining =
	            total - processedCount;

	        const proceed =
	            await confirm({
	                title: "Unprocessed Spaces",
	                message:
	                    "There are still " +
	                    remaining +
	                    " unprocessed spaces.\n\n" +
	                    "Submit anyway?",
	                confirmText: "Submit Anyway",
	                cancelText: "Review Remaining",
	                confirmColor: "#f59e0b"
	            });
				
	        if (!proceed) {
	            return;
	        }
	    }
		
	    await handleSubmit(
	        floorCount,
	        true
	    );
	}
	
	
	

    /**************************************************************************
     * Desk Click Handler
     *************************************************************************/

    async function handleDeskClick(desk, e) {
		
        const type =
            workplaceTypes.find(function (t) {

                return (
                    Number(t.id) ===
                    Number(desk.workplaceTypeId)
                );
            });

        if (
            type &&
            type.multiOccupancy
        ) {
			setPill({
			    x: 0,
			    y: 0,
			    state: null
			});
            setPopupDesk(desk);

            return;
        }

        const current =
            clickStates[desk.id] || 0;

        const next =
            (current + 1) % 4;
			
			setPill({
					    x: e.clientX,
					    y: e.clientY,
					    state: next
					});
					
					
			if (pillTimeoutRef.current) {

			    clearTimeout(pillTimeoutRef.current);
			}

			pillTimeoutRef.current =
			    setTimeout(function () {

			        setPill(null);

			    }, 2200);
        setClickStates(function (prev) {

            return {
                ...prev,
                [desk.id]: next
            };
        });
		setSubmitted(false);
		setShowSubmittedLabel(false);
		setShowSubmitButton(true);
        await saveUtilisation(
            desk,
            next,
            desk.selectedActivityId,
            next === 1 ? 1 : 0
        );
    }

    /**************************************************************************
     * Multi Occupancy Confirm
     *************************************************************************/

    async function handleMultiOccupancyConfirm(
        desk,
        activityId,
        state,
        capacity
    ) {
		
		setSubmitted(false);
		setShowSubmittedLabel(false);
		setShowSubmitButton(true);
        await saveUtilisation(
            desk,
            state,
            activityId,
            capacity
        );

        setClickStates(function (prev) {

            return {
                ...prev,
                [desk.id]: state
            };
        });

        setWorkplaces(function (prev) {

            return prev.map(function (w) {

                if (w.id === desk.id) {

                    return {
                        ...w,
                        selectedActivityId: activityId,
                        capacity: capacity
                    };
                }

                return w;
            });
        });

        setPopupDesk(null);
    }

    /**************************************************************************
     * Get Fill Colour
     *************************************************************************/

    function getFillColor(desk) {

        if (desk.selectedActivityId) {

            const act =
                activities.find(function (a) {

                    return (
                        a.id ===
                        desk.selectedActivityId
                    );
                });

            return act
                ? act.color
                : "rgba(255,165,0,0.3)";
        }

        const state =
            clickStates[desk.id] || 0;

        switch (state) {

            case 1:
                return "rgba(255,0,0,0.8)";

            case 2:
                return "rgba(0,200,0,0.7)";

            case 3:
                return "rgba(0,0,255,0.7)";

            default:
                return "rgba(255,165,0,0.3)";
        }
    }
	

    /**************************************************************************
     * Survey Progress
     *************************************************************************/

    const total =
        workplaces.length;

    const processedCount =
        workplaces.filter(function (d) {

            return (
                d.selectedActivityId ||
                (
                    clickStates[d.id] &&
                    clickStates[d.id] > 0
                )
            );

        }).length;

    const processedPercent =
        total > 0
            ? Math.round(
                (processedCount / total) * 100
            )
            : 0;

    /**************************************************************************
     * Render
     *************************************************************************/

   

    return (

        <div
            style={{
                display: "flex",
                flexDirection: "column",
                width: "100vw",
                height: "100vh"
            }}
        >

            <LeftMenu />

            <FloorHeader
                floorName={floorName}
                showUnprocessed={showUnprocessed}
                setShowUnprocessed={setShowUnprocessed}
                processedPercent={processedPercent}
                processedCount={processedCount}
                total={total}
                showSubmitButton={showSubmitButton}
                showSubmittedLabel={showSubmittedLabel}
                handleSubmit={handleSubmitClick}
                floorCount={floorCount}
				observationHour={observationHour}
            />

            <SurveyProgress/>

            <FloorCanvas
                workplaces={workplaces}
                clickStates={clickStates}
                showUnprocessed={showUnprocessed}
                handleDeskClick={handleDeskClick}
                getFillColor={getFillColor}
                imgUrl={imgUrl}
                floorPlanWidth={floorPlanWidth}
                floorPlanHeight={floorPlanHeight}
                containerRef={containerRef}
                initialScale={initialScale}
            />

			<FloorPopupController

			    popupDesk={popupDesk}
			   

			    activities={activities}

			    workplaces={workplaces}
			    setWorkplaces={setWorkplaces}

			    setPopupDesk={setPopupDesk}

			    onConfirm={handleMultiOccupancyConfirm}

			/>
			
			<ConfirmationModal
			    {...modalProps}
			    onConfirm={handleModalConfirm}
			    onCancel={handleModalCancel}
			/>
			{confirmModal}
			{
			    pill && (

			        <div
			            style={{
			                position: "fixed",
			                left: pill.x + 20,
			                top: pill.y - 10,
							background:
							    pill.state === 1
							        ? "#ef4444"
							        : pill.state === 2
							            ? "#22c55e"
							            : pill.state === 3
							                ? "#3b82f6"
							                : "rgba(0,0,0,0)",
			                color: "white",
							padding:
							    pill.state === 1 ||
							    pill.state === 2 ||
							    pill.state === 3
							        ? "8px 18px"
							        : "0px",
			                borderRadius: "18px",
			                fontWeight: 700,
			                fontSize: "13px",
			                zIndex: 9999999,
			                pointerEvents: "none",
							boxShadow:
							    pill.state === 1 ||
							    pill.state === 2 ||
							    pill.state === 3
							        ? "0 6px 20px rgba(0,0,0,0.28)"
							        : "none",
			                animation:
			                    "pillPop 1.2s ease"
			            }}
			        >
					{
					    pill.state === 1
					        ? "Occupied"
					        : pill.state === 2
					            ? "Vacant"
					            : pill.state === 3
					                ? "SOL"
					                : ""
					}
			        </div>
			    )
			}
        </div>
    );
}